import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Copy, Check as CheckIcon, ExternalLink, Download, Share2, ArrowLeft, FileText } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const QuotePreview = () => {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/quote-preview/${quoteId}`).then((r) => {
      if (alive) setQuote(r.data);
    }).catch(() => {
      if (alive) setNotFound(true);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [quoteId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("Preview link copied.");
    setTimeout(() => setCopied(false), 2000);
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `wasntme.party — Branded preview for ${quote?.company || "your team"}`,
          text: "Here's the branded Case Closed certificate preview.",
          url: window.location.href,
        });
      } else {
        copyLink();
      }
    } catch {
      // user cancelled — ignore
    }
  };

  const downloadPng = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/quote-preview/${quoteId}/certificate.png`);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wasntme-preview-${quote?.company?.replace(/\s+/g, "-").toLowerCase() || quoteId}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded.");
    } catch {
      toast.error("Download failed.");
    }
  };

  if (loading) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#d4af37]" />
      </div>
    );
  }

  if (notFound || !quote) {
    return (
      <div className="grain min-h-screen flex items-center justify-center px-6" data-testid="quote-preview-not-found">
        <div className="card-noir p-8 max-w-md w-full text-center">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">lost page</div>
          <h1 className="font-display text-3xl mb-3">Preview not found</h1>
          <p className="text-[#a39797] mb-6">
            This preview link may have expired or the quote ID is wrong. Ask your wasntme.party contact for a fresh link.
          </p>
          <Link to="/" className="btn-primary"><ArrowLeft size={14} /> Back to wasntme.party</Link>
        </div>
      </div>
    );
  }

  const certUrl = `${API_BASE}/api/quote-preview/${quoteId}/certificate.png`;
  const amount = (quote.amount_due_cents / 100).toLocaleString(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  });

  return (
    <div className="grain min-h-screen" data-testid="quote-preview-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <Link to="/for-teams" className="text-[#a39797] hover:text-[#f4f0ec] text-sm" data-testid="preview-back">
            ← Back to For Teams
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={copyLink} className="btn-ghost text-sm" data-testid="preview-copy">
              {copied ? <><CheckIcon size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
            </button>
            <button onClick={share} className="btn-ghost text-sm" data-testid="preview-share">
              <Share2 size={14} /> Share
            </button>
            <button onClick={downloadPng} className="btn-primary text-sm" data-testid="preview-download">
              <Download size={14} /> Download PNG
            </button>
          </div>
        </div>

        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">brand review</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">
          Preview for <span style={{ color: quote.brand_hex || "#8b0000" }}>{quote.company}</span>
        </h1>
        <p className="text-[#a39797] mb-10 max-w-2xl">
          This is the actual 1080×1920 certificate every player will post to their Story once the game ends.
          Forward this link to marketing or stakeholders before signing off on the invoice.
        </p>

        <div className="grid lg:grid-cols-[320px_1fr] gap-10 items-start">
          {/* Side rail: metadata */}
          <div className="card-noir p-6 space-y-5" data-testid="preview-metadata">
            <div>
              <div className="text-xs tracking-widest text-[#a39797] mb-1">COMPANY</div>
              <div className="font-display text-xl text-[#f4f0ec]">{quote.company}</div>
              <div className="text-sm text-[#a39797]">{quote.contact_name}</div>
            </div>
            <div>
              <div className="text-xs tracking-widest text-[#a39797] mb-1">BRAND COLOR</div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded border border-[#2d2424]"
                  style={{ backgroundColor: quote.brand_hex || "#8b0000" }} />
                <span className="font-mono text-sm text-[#f4f0ec]">{quote.brand_hex || "#8b0000 (default)"}</span>
              </div>
            </div>
            <div>
              <div className="text-xs tracking-widest text-[#a39797] mb-1">EVENT</div>
              <div className="text-sm text-[#f4f0ec]">{quote.party} · {quote.people_count} people</div>
            </div>
            <div className="pt-4 border-t border-[#2d2424]">
              <div className="text-xs tracking-widest text-[#a39797] mb-1">QUOTE</div>
              <div className="font-display text-3xl text-[#f4f0ec]">{amount}</div>
              <div className="text-xs text-[#a39797]">NET {quote.net_days} days</div>
            </div>
            {quote.hosted_invoice_url && (
              <a href={quote.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                className="btn-primary w-full justify-center text-sm" data-testid="preview-view-invoice">
                <ExternalLink size={14} /> View Stripe invoice
              </a>
            )}
            {quote.proposal_pdf_url && (
              <a href={`${API_BASE}${quote.proposal_pdf_url}`} target="_blank" rel="noopener noreferrer"
                className="btn-ghost w-full justify-center text-sm mt-2" data-testid="preview-download-proposal">
                <FileText size={14} /> Download 1-page proposal PDF
              </a>
            )}
          </div>

          {/* The certificate itself */}
          <div className="flex justify-center">
            <img
              src={certUrl}
              alt={`Branded Case Closed preview for ${quote.company}`}
              className="w-full max-w-[380px] rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] border border-[#2d2424]"
              data-testid="preview-certificate-img"
            />
          </div>
        </div>

        <div className="mt-12 text-center text-xs text-[#a39797]">
          Rendered live at 1080×1920 — exactly what guests will share. Links are UUID-gated; share only with people you'd invite to the dinner.
        </div>
      </div>
    </div>
  );
};

export default QuotePreview;
